import { supabase } from '../lib/supabase';

export const notificationService = {
  async getAll(userId) {
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });
      
      if (error) {
        // Handle schema cache error specifically
        if (error?.code === 'PGRST205') {
          console.warn('Schema cache not refreshed yet. The notifications table exists but PostgREST needs to refresh its cache. This usually resolves automatically within a few minutes.');
          return [];
        }
        throw error;
      }
      
      return data?.map(row => ({
        id: row?.id,
        userId: row?.user_id,
        type: row?.type,
        severity: row?.severity,
        title: row?.title,
        message: row?.message,
        transactionId: row?.transaction_id,
        amount: row?.amount,
        paymentMethod: row?.payment_method,
        merchant: row?.merchant,
        isRead: row?.is_read,
        createdAt: row?.created_at
      }));
    } catch (err) {
      console.error('Error fetching notifications:', err);
      return [];
    }
  },

  async getUnreadCount(userId) {
    try {
      const { count, error } = await supabase?.from('notifications')?.select('*', { count: 'exact', head: true })?.eq('user_id', userId)?.eq('is_read', false);
      
      if (error) {
        if (error?.code === 'PGRST205') {
          return 0;
        }
        throw error;
      }
      return count || 0;
    } catch (err) {
      console.error('Error fetching unread count:', err);
      return 0;
    }
  },

  async markAsRead(notificationId, userId) {
    const { data, error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', notificationId)?.eq('user_id', userId)?.select()?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      userId: data?.user_id,
      type: data?.type,
      severity: data?.severity,
      title: data?.title,
      message: data?.message,
      transactionId: data?.transaction_id,
      amount: data?.amount,
      paymentMethod: data?.payment_method,
      merchant: data?.merchant,
      isRead: data?.is_read,
      createdAt: data?.created_at
    };
  },

  async markAllAsRead(userId) {
    const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('user_id', userId)?.eq('is_read', false);
    
    if (error) throw error;
  },

  subscribeToNotifications(userId, callback) {
    const channel = supabase?.channel('notifications')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const notification = {
            id: payload?.new?.id,
            userId: payload?.new?.user_id,
            type: payload?.new?.type,
            severity: payload?.new?.severity,
            title: payload?.new?.title,
            message: payload?.new?.message,
            transactionId: payload?.new?.transaction_id,
            amount: payload?.new?.amount,
            paymentMethod: payload?.new?.payment_method,
            merchant: payload?.new?.merchant,
            isRead: payload?.new?.is_read,
            createdAt: payload?.new?.created_at
          };
          callback(notification);
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  },

  // Browser push notification support
  async requestPushPermission() {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  },

  async showBrowserNotification(title, options = {}) {
    const hasPermission = await this.requestPushPermission();
    
    if (!hasPermission) {
      console.warn('Notification permission not granted');
      return;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options
      });

      // Auto-close after 5 seconds
      setTimeout(() => notification?.close(), 5000);

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  },

  getPushPermissionStatus() {
    if (!('Notification' in window)) {
      return 'unsupported';
    }
    return Notification.permission;
  }
};